import { useState } from "react";
import { useApi, Card, StatusBadge, Loading, Empty, Pager, TableWrap } from "../common";
import { fdatetime, todayIST } from "../../lib/format";
import { BarList } from "./shared";

function DailySummary({ tick }) {
  const [day, setDay] = useState(todayIST());
  const { data } = useApi(`/leadgen/summary?day=${day}`, [tick]);
  const stat = (l, v, t) => <div className="rounded-lg bg-muted/50 p-3" data-testid={`summary-${t}`}><div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{l}</div><div className="font-num text-xl font-bold mt-1">{v}</div></div>;
  return (
    <Card title="Daily summary (from database)" testid="leadgen-daily-summary" actions={<input type="date" className="vmw-input w-40" value={day} onChange={(e) => setDay(e.target.value)} data-testid="summary-date" />}>
      {!data ? <Loading /> : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2">
            {stat("Runs", data.runs, "runs")}{stat("Prospects discovered", data.total_discovered, "discovered")}{stat("New unique leads", data.new_unique, "new")}
            {stat("Duplicates prevented", data.duplicates_prevented, "duplicates")}{stat("High priority", data.high_priority, "high")}
            {stat("Needs verification", data.needs_verification, "verify")}{stat("Assigned to sales", data.assigned_total, "assigned")}
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div><div className="text-sm font-semibold mb-2">New leads by state</div><BarList data={data.by_state} testid="summary-by-state" empty="None" /></div>
            <div><div className="text-sm font-semibold mb-2">New leads by city</div><BarList data={data.by_city} testid="summary-by-city" empty="None" /></div>
            <div><div className="text-sm font-semibold mb-2">Assigned per sales executive</div><BarList data={data.assigned} testid="summary-assigned" empty="None" /></div>
          </div>
          <div data-testid="summary-failed-sources"><div className="text-sm font-semibold mb-1">Sources that failed or need configuration</div>
            {data.failed_sources.length === 0 ? <div className="text-sm text-muted-foreground">None</div> : data.failed_sources.map((s) => (
              <div key={s.source} className="text-sm py-1"><span className="font-semibold">{s.source}</span> ({s.count}): <span className="text-rose-700">{s.messages.join(" | ")}</span></div>))}
          </div>
        </div>
      )}
    </Card>
  );
}

function Runs({ tick }) {
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(null);
  const { data } = useApi(`/leadgen/runs?page=${page}&page_size=10`, [tick]);
  if (!data) return <Loading />;
  return (
    <TableWrap>
      {!data.items.length ? <Empty text="No runs yet — press Run Now" /> : (
        <table className="vmw-table" data-testid="leadgen-runs-table"><thead><tr><th>Started</th><th>Trigger</th><th>Status</th><th>Discovered</th><th>New</th><th>Dup. blocked</th><th>Flagged</th><th>Excluded / out of region</th><th>Errors</th></tr></thead>
          <tbody>{data.items.map((r) => { const s = r.stats || {}; return [
            <tr key={r.id} className="cursor-pointer" onClick={() => setOpen(open === r.id ? null : r.id)} data-testid={`run-row-${r.id}`}>
              <td className="whitespace-nowrap">{fdatetime(r.started_at)}</td><td className="capitalize">{r.trigger}{r.filename ? ` · ${r.filename}` : ""}</td><td><StatusBadge value={r.status} /></td>
              <td>{s.discovered ?? "—"}</td><td className="font-semibold">{s.new ?? "—"}</td><td>{s.duplicates_blocked ?? "—"}</td><td>{s.flagged_review ?? "—"}</td>
              <td>{s.excluded_irrelevant ?? 0} / {s.out_of_region ?? 0}</td><td className={r.errors?.length ? "text-rose-600 font-semibold" : ""}>{r.errors?.length || 0}</td></tr>,
            open === r.id && <tr key={`${r.id}-d`}><td colSpan={9} className="bg-muted/40 text-xs space-y-1">
              <div>Sources attempted: {(r.sources_attempted || []).join(", ") || "—"} · API calls: {s.api_calls ?? 0} · Website checks: {s.website_checks ?? 0} · Reverified: {s.reverified ?? 0} · Auto-approved: {s.auto_approved ?? 0}</div>
              {(r.errors || []).map((e) => <div key={`${e.at}-${e.source}-${e.message}`} className="text-rose-700">{e.source}: {e.message}</div>)}
            </td></tr>]; })}</tbody></table>
      )}
      <Pager page={page} total={data.total} pageSize={10} onPage={setPage} />
    </TableWrap>
  );
}

function Decisions({ tick }) {
  const [page, setPage] = useState(1);
  const { data } = useApi(`/leadgen/dedup-decisions?page=${page}`, [tick]);
  if (!data) return <Loading />;
  return (
    <TableWrap>
      {!data.items.length ? <Empty text="No duplicate decisions yet" /> : (
        <table className="vmw-table" data-testid="leadgen-decisions-table"><thead><tr><th>When</th><th>Decision</th><th>Candidate</th><th>Source</th><th>Matched record</th><th>Matched on</th><th>Filled</th></tr></thead>
          <tbody>{data.items.map((d) => (
            <tr key={d.id}><td className="whitespace-nowrap text-xs">{fdatetime(d.at)}</td><td><StatusBadge value={d.decision.replace(/_/g, " ")} /></td>
              <td>{d.candidate.business_name}<div className="text-[11px] text-muted-foreground">{d.candidate.city}</div></td><td className="text-xs">{d.candidate.source}</td>
              <td className="text-xs"><span className="font-semibold">{d.matched?.type} {d.matched?.code}</span> {d.matched?.name}</td>
              <td className="text-xs">{(d.matched?.fields || []).join(", ")}</td><td className="text-xs">{(d.filled_fields || []).join(", ") || "—"}</td></tr>
          ))}</tbody></table>
      )}
      <Pager page={page} total={data.total} pageSize={25} onPage={setPage} />
    </TableWrap>
  );
}

function Errors({ tick }) {
  const { data } = useApi("/leadgen/errors?limit=50", [tick]);
  if (!data) return <Loading />;
  return (
    <TableWrap>
      {!data.length ? <Empty text="No errors logged" /> : (
        <table className="vmw-table" data-testid="leadgen-errors-table"><thead><tr><th>When</th><th>Source</th><th>Message</th></tr></thead>
          <tbody>{data.map((e) => <tr key={e.id}><td className="whitespace-nowrap text-xs">{fdatetime(e.at)}</td><td className="font-semibold">{e.source}</td><td className="text-rose-700 text-sm">{e.message}</td></tr>)}</tbody></table>
      )}
    </TableWrap>
  );
}

export default function RunHistory({ tick }) {
  return (
    <div className="space-y-5" data-testid="leadgen-history">
      <DailySummary tick={tick} />
      <div><h3 className="font-display font-semibold mb-2">Run history</h3><Runs tick={tick} /></div>
      <div><h3 className="font-display font-semibold mb-2">Duplicate decisions log</h3><Decisions tick={tick} /></div>
      <div><h3 className="font-display font-semibold mb-2">Error log</h3><Errors tick={tick} /></div>
    </div>
  );
}
